import { useSupabase } from '../lib/supabase';
import { cacheManager } from '../utils/cacheManager';
import { cloudinaryService } from './cloudinaryService';
import { performanceMonitor, PerformanceMetric } from '../utils/performanceMonitor';
import { CACHE_CONFIG, getOptimalTTL, generateProductCacheKey } from '../utils/cacheConfig';
import {
  cacheInvalidationManager,
  invalidateProductCache,
  invalidateStockCache,
  invalidateProductViewCache,
  invalidateDeletedProductCache,
} from '../utils/cacheInvalidationManager';
import type {
  Product,
  ProductResponse,
  ProductWithRelations,
  ProductsResult,
  CursorPaginationResult,
  ProductStats,
  SimilarProductsResult,
  RankedProduct,
  SortOption,
  RankingSort,
  StockFilter,
  GetByStorePaginatedOptions,
  SearchOptions,
  CreateProductPayload,
  UpdateProductPayload,
} from '../types/product';
import { toProduct } from '../types/product';
import {
  validateProduct,
  getCurrentUser,
  getStoreAndValidateOwnership,
  getProductAndValidateOwnership,
  applyProductRLS,
  rankProductsByScore,
  deduplicateProducts,
  filterByStockStatus,
  filterBySearch,
  sortProducts,
  calculateDiscountPercent,
  getPromotionInfo,
} from '../utils/productUtils';

/**
 * Unified 6-tier product ranking system
 * Ensures consistent product ordering across all display contexts
 * Delegated to productUtils.rankProductsByScore()
 */

export const productService = {
  /**
   * Create a new product
   * ✅ Validated ownership
   * ✅ Typed payload
   */
  async create(payload: CreateProductPayload): Promise<Product> {
    const client = useSupabase();

    // ✅ Validate product data
    const validationErrors = validateProduct(payload);
    if (validationErrors.length > 0) {
      throw new Error(`Product validation failed: ${validationErrors.map(e => `${e.field}: ${e.message}`).join(', ')}`);
    }

    // ✅ Validate ownership of store
    await getStoreAndValidateOwnership(payload.store_id);

    // ✅ Insert and return
    const { data, error } = await client
      .from('products')
      .insert(payload)
      .select('*')
      .single();

    if (error) throw error;
    return data as Product;
  },

  /**
   * Get products by store (active only)
   * ✅ Typed return
   */
  async getByStore(storeId: string): Promise<Product[]> {
    const client = useSupabase();
    const { data, error } = await client
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as Product[];
  },

  /**
   * Get available products (in stock)
   * ✅ Typed return
   */
  async getByStoreAvailable(storeId: string): Promise<Product[]> {
    const client = useSupabase();
    const { data, error } = await client
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .eq('is_active', true)
      .gt('stock', 0)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as Product[];
  },

  /**
   * Get all products for a store (including inactive)
   * ✅ Typed return
   */
  async getByStoreAll(storeId: string): Promise<Product[]> {
    const client = useSupabase();
    const { data, error } = await client
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as Product[];
  },

  /**
   * Get paginated products by store with filters
   * ✅ Typed options and return
   * ✅ Supports stock filter, search, sorting
   */
  async getByStorePaginated(
    storeId: string,
    options: GetByStorePaginatedOptions = {}
  ): Promise<ProductsResult> {
    const client = useSupabase();
    const page = options.page || 0;
    const limit = options.limit || 20;
    const offset = page * limit;

    let query = client
      .from('products')
      .select('*', { count: 'exact' })
      .eq('store_id', storeId);

    if (options.collectionId) {
      query = query.eq('collection_id', options.collectionId);
    }

    if (options.stockFilter && options.stockFilter !== 'all') {
      switch (options.stockFilter) {
        case 'in_stock':
          query = query.gt('stock', 0);
          break;
        case 'low_stock':
          query = query.lte('stock', 10).gt('stock', 0);
          break;
        case 'out_of_stock':
          query = query.eq('stock', 0);
          break;
      }
    }

    if (options.search) {
      const searchPattern = `%${options.search}%`;
      query = query.or(`name.ilike.${searchPattern},reference.ilike.${searchPattern}`);
    }

    if (options.isActive !== undefined) {
      query = query.eq('is_active', options.isActive);
    }

    if (options.sortBy) {
      switch (options.sortBy) {
        case 'name_asc': query = query.order('name', { ascending: true }); break;
        case 'name_desc': query = query.order('name', { ascending: false }); break;
        case 'price_asc': query = query.order('price', { ascending: true }); break;
        case 'price_desc': query = query.order('price', { ascending: false }); break;
        case 'stock_asc': query = query.order('stock', { ascending: true }); break;
        case 'stock_desc': query = query.order('stock', { ascending: false }); break;
        case 'date_asc': query = query.order('created_at', { ascending: true }); break;
        case 'date_desc':
        default: query = query.order('created_at', { ascending: false }); break;
      }
    } else {
      query = query.order('created_at', { ascending: false });
    }

    const { data, error, count } = await query
      .range(offset, offset + limit - 1);

    if (error) throw error;

    const hasMore = (offset + limit) < (count || 0);
    const totalPages = Math.ceil((count || 0) / limit);

    return {
      products: (data || []) as Product[],
      hasMore,
      totalCount: count || 0,
      currentPage: page,
      totalPages,
      limit
    };
  },

  /**
   * Get all products globally (public only) with ranking
   * ✅ Typed parameters and return
   * ✅ Cached with SWR (stale-while-revalidate)
   * ✅ Reduced over-fetch (3× pageSize max, not 10×)
   */
  async getAll(
    page = 0,
    pageSize = 20,
    sort: RankingSort = 'newest'
  ): Promise<Product[]> {
    const cacheKey = `products_all_${page}_${pageSize}_${sort}`;

    const fetcher = async (): Promise<Product[]> => {
      const client = useSupabase();
      const from = page * pageSize;
      const to = from + pageSize - 1;

      // For simple sorts (newest, popular), let Supabase handle it server-side
      const needsClientRanking = ['trending', 'ranked', 'sales', 'top'].includes(sort);

      if (!needsClientRanking) {
        const orderCol = sort === 'popular' ? 'view_count' : 'created_at';
        const { data, error } = await client
          .from('products')
          .select('id, store_id, name, description, price, compare_price, images, view_count, created_at, updated_at, is_active, stock, stores(name, logo_url, category)')
          .eq('is_active', true)
          .gt('stock', 0)
          .order(orderCol, { ascending: false })
          .range(from, to);

        if (error) throw error;
        return ((data || []).map(toProduct)) as unknown as Product[];
      }

      // For composite ranking: fetch 3× pageSize (reduced from 10×)
      const fetchSize = Math.min(pageSize * 3, 60);
      const { data, error } = await client
        .from('products')
        .select('id, store_id, name, description, price, compare_price, images, view_count, created_at, updated_at, is_active, stock, stores(name, logo_url, category)')
        .eq('is_active', true)
        .gt('stock', 0)
        .order('created_at', { ascending: false })
        .range(from, from + fetchSize - 1);

      if (error) throw error;

      // ✅ Use productUtils ranking function
      const mapped = (data || [])
        .map(toProduct)
        .filter(p => p.stores?.category !== 'Bars' && p.stores?.category !== 'Bar');
      const ranked = rankProductsByScore(mapped, sort);
      return ranked.slice(0, pageSize);
    };

    const result = await cacheManager.swr(cacheKey, fetcher, { ttl: 5 * 60 * 1000 });
    return result.data || [];
  },

  /**
   * Get products by category (via collections)
   * ⚠️ TODO: This does 4 sequential queries - needs RPC optimization
   * ✅ Typed parameters and return
   * ✅ Reduced over-fetch from 10× to 3×
   */
  async getAllByCategory(
    categoryName: string,
    page = 0,
    pageSize = 20,
    sort: RankingSort = 'newest'
  ): Promise<Product[]> {
    const client = useSupabase();

    try {
      // Query 1: Get category ID by name
      const { data: categoryData, error: catError } = await client
        .from('categories')
        .select('id, name')
        .ilike('name', categoryName)
        .single();

      if (catError || !categoryData?.id) {
        return [];
      }

      const categoryId = categoryData.id;

      // Query 2: Find all subcategories
      const { data: subCategories } = await client
        .from('categories')
        .select('id')
        .eq('parent_id', categoryId);

      const categoryIds = [categoryId];
      if (subCategories && subCategories.length > 0) {
        categoryIds.push(...subCategories.map(c => c.id));
      }

      // Query 3: Get all collections for these categories
      const { data: collections, error: collError } = await client
        .from('collections')
        .select('id, name')
        .in('category_id', categoryIds)
        .eq('is_active', true);

      if (collError) throw collError;

      if (!collections || collections.length === 0) {
        return [];
      }

      const collectionIds = collections.map((c: any) => c.id);

      // Query 4: Fetch products (reduced from 10× to 3× pageSize)
      const fetchSize = Math.min(pageSize * 3, 60);
      const from = page * pageSize;

      const { data: products, error: prodError } = await client
        .from('products')
        .select('id, store_id, name, description, price, compare_price, images, view_count, created_at, updated_at, is_active, stock, stores(name, logo_url, category)')
        .in('collection_id', collectionIds)
        .eq('is_active', true)
        .gt('stock', 0)
        .order('created_at', { ascending: false })
        .range(0, fetchSize - 1);

      if (prodError) throw prodError;

      // ✅ Use productUtils ranking
      const mapped = (products || [])
        .map(toProduct)
        .filter(p => p.stores?.category !== 'Bars' && p.stores?.category !== 'Bar');
      const ranked = rankProductsByScore(mapped, sort);

      return ranked.slice(from, from + pageSize);
    } catch (err) {
      console.error('[ProductService] getAllByCategory error:', err);
      return [];
    }
  },

  /**
   * Cursor-based pagination for infinite scroll with unified ranking
   * ✅ Typed parameters and return
   * ✅ Reduced over-fetch from 15× to 3×
   */
  async getAllWithCursor(
    cursor: string | null,
    pageSize = 8,
    sort: RankingSort = 'newest'
  ): Promise<CursorPaginationResult> {
    const cacheKey = `products_cursor_${cursor}_${pageSize}_${sort}`;

    const fetcher = async (): Promise<CursorPaginationResult> => {
      const client = useSupabase();

      // Reduced from 15× to 3× pageSize for better performance
      const batchSize = Math.min(pageSize * 3, 60);

      let query = client
        .from('products')
        .select('id, store_id, name, description, price, compare_price, images, view_count, created_at, updated_at, is_active, stock, stores(name, logo_url, category)')
        .eq('is_active', true)
        .gt('stock', 0)
        .order('created_at', { ascending: false });

      let allData: Product[] = [];

      // Fetch initial or cursor-based batch
      if (!cursor) {
        const { data, error } = await query.limit(batchSize);
        if (error) throw error;
        allData = (data || []).map(toProduct);
      } else {
        // Decode cursor to get offset
        try {
          const decodedCursor = JSON.parse(atob(cursor));
          const offset = decodedCursor.offset || 0;

          const { data, error } = await query.range(offset, offset + batchSize - 1);
          if (error) throw error;
          allData = (data || []).map(toProduct);
        } catch (e) {
          console.warn('[ProductService] Invalid cursor, fetching from start:', e);
          const { data, error } = await query.limit(batchSize);
          if (error) throw error;
          allData = (data || []).map(toProduct);
        }
      }

      // Filtrer les produits de type Bar
      allData = allData.filter(p => p.stores?.category !== 'Bars' && p.stores?.category !== 'Bar');

      // ✅ Use productUtils ranking
      const rankedData = rankProductsByScore(allData, sort);

      // Return pageSize items from ranked data
      const items = rankedData.slice(0, pageSize);
      const hasMore = rankedData.length > pageSize;

      let nextCursor: string | null = null;
      if (hasMore && items.length > 0) {
        const currentOffset = cursor ? JSON.parse(atob(cursor)).offset || 0 : 0;
        const nextOffset = currentOffset + pageSize;

        nextCursor = btoa(JSON.stringify({ offset: nextOffset, sort }));
      }

      return { data: items, nextCursor, hasMore };
    };

    const result = await cacheManager.swr(cacheKey, fetcher, { ttl: 3 * 60 * 1000 });
    return result.data || { data: [], nextCursor: null, hasMore: false };
  },

  /**
   * Get product by ID
   * ✅ Typed return
   */
  async getById(id: string): Promise<Product | null> {
    const client = useSupabase();
    const { data, error } = await client
      .from('products')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    return data as Product;
  },

  /**
   * Update product
   * ✅ RLS validation (user must own store)
   * ✅ Typed parameters
   * ✅ Versioning for optimistic locking
   */
  async update(id: string, payload: UpdateProductPayload): Promise<Product> {
    const startTime = performance.now();
    
    try {
      // ✅ Validate ownership first
      await getProductAndValidateOwnership(id);

      // ✅ Validate update payload
      const validationErrors = validateProduct(payload);
      if (validationErrors.length > 0) {
        throw new Error(`Product validation failed: ${validationErrors.map(e => `${e.field}: ${e.message}`).join(', ')}`);
      }

      const client = useSupabase();
      const { data, error } = await client
        .from('products')
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .maybeSingle();

      if (error) throw error;
      
      // ✅ Invalidate product cache on update
      await invalidateProductCache(id);
      
      // ✅ Auto-notify waiting clients if stock is now > 0
      if (payload.stock && payload.stock > 0 && data) {
        try {
          const { orderService } = await import('./orderService');
          await orderService.notifyRestockedClients(data.store_id, data.id, data.name);
        } catch (e) {
          console.warn('[ProductService] Auto-notify restocked clients failed:', e);
        }
      }
      
      return data as Product;
    } finally {
      // ✅ Track performance
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric({
        operation: 'productService.update',
        duration,
        timestamp: new Date(),
      });
    }
  },

  /**
   * Soft-delete product (mark as inactive, don't delete)
   * ✅ RLS validation
   * ✅ Audit trail (deleted_at, deleted_by)
   */
  async delete(id: string): Promise<void> {
    const startTime = performance.now();
    
    try {
      const client = useSupabase();
      const user = await getCurrentUser();

      // ✅ Validate ownership first
      const product = await getProductAndValidateOwnership(id);

      // ✅ Soft delete: mark as inactive instead of hard delete
      const { error } = await client
        .from('products')
        .update({
          is_active: false,
          deleted_at: new Date().toISOString(),
          deleted_by: user.id,
        })
        .eq('id', id);

      if (error) throw error;
      
      // ✅ Supprimer les images Cloudinary
      if (product && product.images && product.images.length > 0) {
        // Run in background to avoid blocking
        cloudinaryService.deleteImages(product.images).catch(e => {
          console.warn('[ProductService] Failed to delete Cloudinary images:', e);
        });
      }

      // ✅ Invalidate all related caches on deletion
      await invalidateDeletedProductCache(id);
    } finally {
      // ✅ Track performance
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric({
        operation: 'productService.delete',
        duration,
        timestamp: new Date(),
      });
    }
  },

  /**
   * Get popular products by category
   * ✅ Typed parameters and return
   * ✅ Reduced over-fetch from 10× to 3×
   */
  async getPopularByCategory(category: string, limit: number = 4): Promise<Product[]> {
    const client = useSupabase();

    // Reduced from 10× to 3× pageSize
    const fetchLimit = Math.max(Math.min(limit * 3, 60), 12);

    const { data, error } = await client
      .from('products')
      .select('id, store_id, name, price, compare_price, images, view_count, created_at, updated_at, is_active, stock, stores!inner(category)')
      .eq('stores.is_active', true)
      .eq('is_active', true)
      .gt('stock', 0)
      .order('created_at', { ascending: false })
      .limit(fetchLimit);

    if (error) throw error;

    // Filter by category (case-insensitive, normalized)
    const categoryNorm = category.toLowerCase().trim();
    const filtered = (data || [])
      .map(toProduct)
      .filter((p: Product) =>
        p.stores &&
        (p.stores.category?.toLowerCase().trim() === categoryNorm)
      );

    // ✅ Use productUtils ranking
    const ranked = rankProductsByScore(filtered, 'popular');

    return ranked.slice(0, limit);
  },

  /**
   * Get store homepage featured products
   * ✅ Typed return
   * ✅ Fallback to recent if no featured
   */
  async getStoreHomepageProducts(storeId: string, limit = 8): Promise<Product[]> {
    const client = useSupabase();

    console.log('Getting homepage products for store:', storeId);

    // Only return products explicitly marked as featured by the seller
    const { data: featured, error: featuredError } = await client
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .eq('featured', true)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    console.log('Featured products result:', featured?.length, featuredError);

    if (featuredError && featuredError.code !== 'PGRST116') {
      throw featuredError;
    }

    return (featured || []) as Product[];
  },

  /**
   * Get featured product count for store
   * ✅ Typed return
   */
  async getFeaturedCount(storeId: string): Promise<number> {
    const client = useSupabase();
    const { count, error } = await client
      .from('products')
      .select('*', { count: 'exact', head: true })
      .eq('store_id', storeId)
      .eq('featured', true);

    if (error) throw error;
    return count || 0;
  },

  /**
   * Get store products with active promotions
   * ✅ Typed return
   * ✅ Calculates discount percentages
   */
  async getStorePromotionProducts(storeId: string): Promise<Product[]> {
    const client = useSupabase();

    // Get all active products for the store
    const { data: allProducts, error } = await client
      .from('products')
      .select('*')
      .eq('store_id', storeId)
      .eq('is_active', true);

    if (error) throw error;

    // Filter products with promotions on client side
    const promotionProducts = (allProducts || []).filter((product: Product) => {
      // Product has active sale
      if (product.sale_active === true) return true;

      // Product has compare_price greater than price (indicates discount)
      if (product.compare_price && product.compare_price > product.price) return true;

      return false;
    });

    // Calculate discount percent for products with compare_price but no discount_percent
    const productsWithDiscount = promotionProducts.map((product: Product) => {
      if (!product.discount_percent && product.compare_price && product.compare_price > product.price) {
        const discountPercent = Math.round(((product.compare_price - product.price) / product.compare_price) * 100);
        return { ...product, discount_percent: discountPercent };
      }
      return product;
    });

    // Sort by discount percent (highest first)
    productsWithDiscount.sort((a: Product, b: Product) => {
      const discountA = a.discount_percent || 0;
      const discountB = b.discount_percent || 0;
      return discountB - discountA;
    });

    return productsWithDiscount as Product[];
  },

  /**
   * Search products by query
   * ✅ Typed parameters and return
   * ✅ Supports category and collection filters
   */
  async search(
    query: string,
    page = 0,
    pageSize = 20,
    options?: { category?: string; collection_id?: string }
  ): Promise<Product[]> {
    const client = useSupabase();
    const normalizedQuery = query.trim();
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const searchPattern = `%${normalizedQuery}%`;

    let dbQuery = client
      .from('products')
      .select('*, stores(name, logo_url, category)')
      .or(
        `name.ilike.${searchPattern},description.ilike.${searchPattern},category.ilike.${searchPattern},reference.ilike.${searchPattern}`
      )
      .eq('is_active', true)
      .gt('stock', 0);

    if (options?.category) dbQuery = dbQuery.eq('category', options.category);
    if (options?.collection_id) dbQuery = dbQuery.eq('collection_id', options.collection_id);

    const { data, error } = await dbQuery
      .order('created_at', { ascending: false })
      .limit(pageSize * 3); // Fetch more to compensate for client-side filtering
    if (error) throw error;
    
    const mapped = (data || []).filter((p: any) => p.stores?.category !== 'Bars' && p.stores?.category !== 'Bar');
    return mapped.slice(from, to + 1) as Product[];
  },

  /**
   * Increment product view count
   * ✅ FIXED: Now uses PostgreSQL RPC for atomicity (no race condition)
   */
  async incrementViews(productId: string): Promise<void> {
    const startTime = performance.now();
    const client = useSupabase();
    let rpcUsed = false;
    
    try {
      // ✅ USE RPC: Atomic operation - no race condition possible
      const { error } = await client
        .rpc('increment_product_views', {
          product_id: productId
        });

      if (error) {
        console.warn('[ProductService] RPC increment_product_views failed, falling back:', error);
        // Fallback to non-atomic update if RPC not available
        await client
          .from('products')
          .update({ view_count: 1 }) // Conservative: just mark as viewed
          .eq('id', productId);
      } else {
        rpcUsed = true;
      }
      
      // ✅ Invalidate stats cache when views change
      await invalidateProductViewCache(productId);
    } catch (err) {
      // Non-critical: silently ignore
      console.debug('[ProductService] incrementViews skipped:', err);
    } finally {
      // ✅ Track performance
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric({
        operation: 'productService.incrementViews',
        duration,
        timestamp: new Date(),
        rpcUsed,
      });
    }
  },

  /**
   * Get product statistics (views, likes, sales)
   * ✅ Typed return
   * ✅ Parallel queries for performance
   */
  async getProductStats(productId: string): Promise<ProductStats> {
    const client = useSupabase();
    try {
      // Execute all counts in parallel for performance
      const [productRes, likesRes, salesRes] = await Promise.all([
        client.from('products').select('view_count').eq('id', productId).maybeSingle(),
        client.from('product_likes').select('id', { count: 'exact', head: true }).eq('product_id', productId),
        client.from('order_items').select('quantity, orders(status)').eq('product_id', productId)
      ]);

      // Only count sales from successful orders (paid, shipped, or delivered)
      const confirmedStatuses = ['paid', 'shipped', 'delivered'];
      const totalSales = (salesRes.data || [])
        .filter((item: any) => item.orders && confirmedStatuses.includes(item.orders.status))
        .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

      return {
        views: Number(productRes.data?.view_count) || 0,
        likes: likesRes.count || 0,
        sales: totalSales
      };
    } catch (err) {
      console.error('[ProductService] getProductStats error:', err);
      return { views: 0, likes: 0, sales: 0 };
    }
  },

  /**
   * Record product sale
   * ✅ Non-critical fallback (primary handled by orderService RPC)
   */
  async recordSale(productId: string, quantity = 1): Promise<void> {
    const client = useSupabase();
    try {
      // This is primarily handled by orderService.confirmPayment RPC
      // Keeping as safe fallback
      await client
        .from('products')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', productId);
    } catch (err) {
      // Silently fail - handled by RPC
      console.debug('[ProductService] recordSale skipped:', err);
    }
  },

  /**
   * Update product stock
   * ✅ FIXED: Now uses PostgreSQL RPC for atomicity (no race condition)
   */
  async updateStock(productId: string, quantityToAdd: number): Promise<Product> {
    const startTime = performance.now();
    const client = useSupabase();
    let rpcUsed = false;

    try {
      // ✅ USE RPC: Atomic operation - prevents race conditions
      const { data: rpcResult, error: rpcError } = await client
        .rpc('increment_product_stock', {
          product_id: productId,
          quantity: quantityToAdd
        });

      if (rpcError) {
        console.warn('[ProductService] RPC increment_product_stock failed, falling back:', rpcError);
        
        // Fallback to traditional approach if RPC not available
        const { data: product, error: fetchError } = await client
          .from('products')
          .select('stock')
          .eq('id', productId)
          .single();

        if (fetchError) throw fetchError;

        const newStock = Math.max(0, (product?.stock || 0) + quantityToAdd);

        const { data: updated, error: updateError } = await client
          .from('products')
          .update({ stock: newStock, updated_at: new Date().toISOString() })
          .eq('id', productId)
          .select('*')
          .single();

        if (updateError) throw updateError;
        
        // ✅ Invalidate stock cache when updated via fallback
        await invalidateStockCache(productId);
        
        // ✅ Auto-notify waiting clients if stock is now > 0
        if (newStock > 0 && updated) {
          try {
            const { orderService } = await import('./orderService');
            await orderService.notifyRestockedClients(updated.store_id, updated.id, updated.name);
          } catch (e) {
            console.warn('[ProductService] Auto-notify restocked clients failed:', e);
          }
        }
        
        return updated as Product;
      }

      rpcUsed = true;
      
      // ✅ RPC successful - return updated product
      if (rpcResult && rpcResult.length > 0) {
        const result = rpcResult[0];
        // Fetch full product since RPC returns minimal fields
        const { data: fullProduct, error: fetchError } = await client
          .from('products')
          .select('*')
          .eq('id', result.id)
          .single();

        if (fetchError) throw fetchError;
        
        // ✅ Invalidate stock cache when updated via RPC
        await invalidateStockCache(productId);
        
        // ✅ Auto-notify waiting clients if stock is now > 0
        if (fullProduct && fullProduct.stock > 0) {
          try {
            const { orderService } = await import('./orderService');
            await orderService.notifyRestockedClients(fullProduct.store_id, fullProduct.id, fullProduct.name);
          } catch (e) {
            console.warn('[ProductService] Auto-notify restocked clients failed:', e);
          }
        }
        
        return fullProduct as Product;
      }

      throw new Error(`Product ${productId} not found`);
    } catch (err) {
      console.error('[ProductService] updateStock error:', err);
      throw err;
    } finally {
      // ✅ Track performance
      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric({
        operation: 'productService.updateStock',
        duration,
        timestamp: new Date(),
        rpcUsed,
      });
    }
  },

  /**
   * Get product options/variants
   * ✅ Typed return
   */
  async getProductOptions(productId: string): Promise<any[]> {
    const client = useSupabase();
    try {
      const { data, error } = await client
        .from('product_options')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[ProductService] getProductOptions error:', err);
      return [];
    }
  },

  /**
   * Get similar products
   * ✅ FIXED: Now uses optimized PostgreSQL RPC with UNION (no N+1 queries)
   * ✅ Typed parameters and return
   */
  async getSimilarProducts(product: Product, limit = 6): Promise<Product[]> {
    const client = useSupabase();
    try {
      if (!product.category || !product.store_id) {
        return [];
      }

      // ✅ USE RPC: Single query with UNION - replaces 3 sequential queries
      const { data: rpcResult, error: rpcError } = await client
        .rpc('get_similar_products', {
          p_product_id: product.id,
          p_limit: limit
        });

      if (rpcError) {
        console.warn('[ProductService] RPC get_similar_products failed, falling back to sequential queries:', rpcError);
        
        // Fallback to 3 sequential queries if RPC not available
        const collected: Product[] = [];

        // Query 1: Products from same collection
        if ((product as any).collection_id) {
          const { data: collData, error: collError } = await client
            .from('products')
            .select('*')
            .eq('collection_id', (product as any).collection_id)
            .eq('is_active', true)
            .neq('id', product.id)
            .order('view_count', { ascending: false })
            .limit(limit);

          if (collError) throw collError;
          if (collData && collData.length) collected.push(...collData.map(toProduct));
        }

        // Query 2: Same store & category if needed
        if (collected.length < limit) {
          const remaining = limit - collected.length;
          const { data: storeData, error: storeError } = await client
            .from('products')
            .select('*')
            .eq('category', product.category)
            .eq('is_active', true)
            .neq('id', product.id)
            .eq('store_id', product.store_id)
            .order('view_count', { ascending: false })
            .limit(remaining);

          if (storeError) throw storeError;
          if (storeData && storeData.length) collected.push(...storeData.map(toProduct));
        }

        // Query 3: Other stores in same category if needed
        if (collected.length < limit) {
          const remaining = limit - collected.length;
          const { data: otherData, error: otherError } = await client
            .from('products')
            .select('*')
            .eq('category', product.category)
            .eq('is_active', true)
            .neq('id', product.id)
            .neq('store_id', product.store_id)
            .limit(remaining);

          if (otherError) throw otherError;
          if (otherData && otherData.length) collected.push(...otherData.map(toProduct));
        }

        // Deduplicate and return up to limit
        const unique: Product[] = [];
        const seen = new Set<string>();
        for (const p of collected) {
          if (!p || !p.id) continue;
          if (p.id === product.id) continue;
          if (seen.has(String(p.id))) continue;
          seen.add(String(p.id));
          unique.push(p);
          if (unique.length >= limit) break;
        }

        return unique;
      }

      // ✅ RPC successful - map results to Product
      if (rpcResult && Array.isArray(rpcResult)) {
        return (rpcResult as any[]).map(row => ({
          id: row.id,
          store_id: row.store_id,
          name: row.name,
          description: row.description,
          price: row.price,
          compare_price: row.compare_price,
          images: row.images,
          view_count: row.view_count,
          created_at: row.created_at,
          updated_at: row.updated_at,
          is_active: row.is_active,
          stock: row.stock,
          collection_id: row.collection_id,
          category: row.category,
        }));
      }

      return [];
    } catch (err) {
      console.error('[ProductService] getSimilarProducts error:', err);
      return [];
    }
  }
};

/**
 * Phase 1c: Cache and Performance Monitoring Utilities
 * ✅ Export monitoring functions for use in components and other services
 */

/**
 * Get performance report for productService operations
 * Call this to analyze performance metrics
 */
export function getProductServicePerformanceReport(): string {
  return performanceMonitor.generateReport();
}

/**
 * Get performance stats for a specific operation
 */
export function getProductServiceOperationStats(operation: string) {
  return performanceMonitor.getStats(operation);
}

/**
 * Export metrics for analytics/dashboard
 */
export function exportProductServiceMetrics() {
  return performanceMonitor.exportMetrics();
}

/**
 * Get cache statistics
 */
export async function getProductCacheStats() {
  return await cacheInvalidationManager.getCacheStats();
}

/**
 * Warm cache for popular categories on app startup
 * Call this in your App startup logic for better performance
 */
export async function warmProductCacheForStartup(categories: string[]): Promise<void> {
  await cacheInvalidationManager.warmCacheForPopularCategories(categories);
}

/**
 * Subscribe to cache invalidation events
 * Useful for updating UI when caches are invalidated
 */
export function subscribeToProductCacheEvents(callback: (event: any) => void) {
  return cacheInvalidationManager.subscribe(callback);
}

/**
 * Get optimal cache TTL for a data type
 */
export function getProductDataTTL(dataType: keyof typeof CACHE_CONFIG, context?: any) {
  const config = CACHE_CONFIG[dataType];
  if (!config) return 5 * 60 * 1000; // Default 5 minutes
  return getOptimalTTL(config.volatility, context);
}


